import React, { useState, useEffect } from 'react';

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const RequestForm = () => {
  const [providers, setProviders] = useState([]);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [allUserRequests, setAllUserRequests] = useState([]); // Store all requests
  const [userRequests, setUserRequests] = useState([]); // Store paginated requests for display
  const [activatingRequest, setActivatingRequest] = useState(null);
  const [deletingRequest, setDeletingRequest] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [requestPagination, setRequestPagination] = useState({ total: 0, limit: 10, offset: 0, pages: 0 });

  // Fetch providers on component mount
  useEffect(() => {
    fetchProviders();
    fetchAllUserRequests(); // Fetch all requests once on mount
  }, []);

  // Update paginated display when pagination changes
  useEffect(() => {
    updatePaginatedRequests();
  }, [allUserRequests, requestPagination.offset, requestPagination.limit]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/data/providers`, {
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setProviders(result.data);
        setError(null);
      } else {
        setError(result.message || 'Failed to fetch providers');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error fetching providers:', err);
    } finally {
      setLoading(false);
    }
  };
  

  // Fetch all user requests from API (no pagination parameters)
  const fetchAllUserRequests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/data/requests`, {
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setAllUserRequests(result.data);
        // Update pagination state with total count
        setRequestPagination(prev => ({
          ...prev,
          total: result.data.length,
          pages: Math.ceil(result.data.length / prev.limit)
        }));
      }
    } catch (err) {
      console.error('Error fetching user requests:', err);
    }
  };

  // Update the paginated display based on current pagination settings
  const updatePaginatedRequests = () => {
    const startIndex = requestPagination.offset;
    const endIndex = startIndex + requestPagination.limit;
    const paginatedData = allUserRequests.slice(startIndex, endIndex);
    setUserRequests(paginatedData);
  };

  const handleProviderToggle = (providerName) => {
    setSelectedProviders(prev => {
      if (prev.includes(providerName)) {
        return prev.filter(p => p !== providerName);
      } else {
        return [...prev, providerName];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProviders.length === providers.length) {
      setSelectedProviders([]);
    } else {
      setSelectedProviders(providers.map(p => p.name));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedProviders.length === 0) {
      setError('Please select at least one provider');
      return;
    }
    
    if (searchTerm.trim().length < 3) {
      setError('Search term must be at least 3 characters long');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`${API_BASE_URL}/api/data/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          search_term: searchTerm.trim(),
          providers: selectedProviders,
          info: info.trim(),
          type: 'scrape'
        })
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Request submitted successfully! The bot has been notified and will start collecting data.');
        // Reset form
        setSearchTerm('');
        setSelectedProviders([]);
        setInfo('');
        // Refresh user requests
        fetchAllUserRequests();
      } else {
        setError(result.message || 'Failed to submit request');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error submitting request:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getProviderProperties = (provider) => {
    if (!provider.properties || provider.properties.length === 0) {
      return 'General real estate data';
    }
    return provider.properties.join(', ');
  };

  // Filter providers based on search
  const filteredProviders = providers.filter(provider => {
    if (!providerFilter) return true;
    const searchLower = providerFilter.toLowerCase();
    return (
      provider.name.toLowerCase().includes(searchLower) ||
      getProviderProperties(provider).toLowerCase().includes(searchLower)
    );
  });

  const handleSelectAllFiltered = () => {
    const filteredProviderNames = filteredProviders.map(p => p.name);
    const allFilteredSelected = filteredProviderNames.every(name => selectedProviders.includes(name));
    
    if (allFilteredSelected) {
      // Deselect all filtered providers
      setSelectedProviders(prev => prev.filter(name => !filteredProviderNames.includes(name)));
    } else {
      // Select all filtered providers
      setSelectedProviders(prev => {
        const newSelected = [...prev];
        filteredProviderNames.forEach(name => {
          if (!newSelected.includes(name)) {
            newSelected.push(name);
          }
        });
        return newSelected;
      });
    }
  };

  const handleActivateRequest = async (requestId) => {
    try {
      setActivatingRequest(requestId);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/data/requests/${requestId}/activate`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Refresh user requests to get updated active status
        await fetchAllUserRequests();
        setSuccess('Request activated successfully! Bot has been notified.');
      } else {
        setError(result.message || 'Failed to activate request');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error activating request:', err);
    } finally {
      setActivatingRequest(null);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      setDeletingRequest(requestId);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/data/requests/${requestId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Refresh user requests to get updated list
        await fetchAllUserRequests();
        setSuccess('Request deleted successfully!');
        setShowDeleteConfirm(null); // Close confirmation dialog
      } else {
        setError(result.message || 'Failed to delete request');
        setShowDeleteConfirm(null); // Close confirmation dialog on error too
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error deleting request:', err);
      setShowDeleteConfirm(null); // Close confirmation dialog on error too
    } finally {
      setDeletingRequest(null);
    }
  };

  const handleRequestPageChange = (newOffset) => {
    setRequestPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const handleItemsPerPageChange = (newLimit) => {
    setRequestPagination(prev => {
      const newPages = Math.ceil(prev.total / newLimit);
      return { 
        ...prev, 
        limit: newLimit, 
        offset: 0, // Reset to first page when changing items per page
        pages: newPages
      };
    });
  };

  if (loading && providers.length === 0) {
    return (
      <div className="form-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading providers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="request-form-container">
      <div className="form-header">
        <h2>Submit New Data Request</h2>
        <p className="form-subtitle">
          Request the bot to collect real estate data based on your keywords and selected providers
        </p>
      </div>

      <form onSubmit={handleSubmit} className="request-form-grid">
        {/* Search Term Card */}
        <div className="form-card search-term-card">
          <div className="card-header">
            <h3 className="card-title">🔍 Search Term</h3>
          </div>
          <div className="card-content">
            <label htmlFor="searchTerm" className="form-label">
              What are you looking for? <span className="required">*</span>
            </label>
            <input
              type="text"
              id="searchTerm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="e.g., apartments in New York, condos Miami, houses for sale Boston"
              className="form-input"
              maxLength={255}
              required
            />
            <small className="form-hint">
              Enter keywords that describe what you want to search for (minimum 3 characters)
            </small>
          </div>
        </div>

        {/* Provider Selection Card */}
        <div className="form-card providers-card">
          <div className="card-header">
            <h3 className="card-title">🏢 Select Providers</h3>
            <span className="required">*</span>
          </div>
          <div className="card-content">
            <div className="providers-controls">
              <input
                type="text"
                placeholder="Search providers..."
                value={providerFilter}
                onChange={(e) => setProviderFilter(e.target.value)}
                className="provider-search"
              />
              <button 
                type="button"
                onClick={providerFilter ? handleSelectAllFiltered : handleSelectAll}
                className="select-all-btn"
              >
                {providerFilter 
                  ? (filteredProviders.every(p => selectedProviders.includes(p.name)) ? 'Deselect Filtered' : 'Select Filtered')
                  : (selectedProviders.length === providers.length ? 'Deselect All' : 'Select All')
                }
              </button>
            </div>
            
            {providers.length === 0 ? (
              <div className="no-providers">
                <p>⚠️ No providers available</p>
                <button type="button" onClick={fetchProviders}>Refresh Providers</button>
              </div>
            ) : (
              <>
                {filteredProviders.length === 0 ? (
                  <div className="no-providers">
                    <p>🔍 No providers match your search</p>
                    <p>Try adjusting your search terms</p>
                  </div>
                ) : (
                  <div className="providers-grid">
                    {filteredProviders.map((provider) => (
                      <div 
                        key={provider.id} 
                        className={`provider-card ${selectedProviders.includes(provider.name) ? 'selected' : ''}`}
                        onClick={() => handleProviderToggle(provider.name)}
                      >
                        <div className="provider-header">
                          <input
                            type="checkbox"
                            checked={selectedProviders.includes(provider.name)}
                            onChange={() => handleProviderToggle(provider.name)}
                            className="provider-checkbox"
                          />
                          <h4 className="provider-name">{provider.name}</h4>
                        </div>
                        
                        {provider.link && (
                          <div className="provider-link">
                            <a 
                              href={provider.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              🔗 Visit Website
                            </a>
                          </div>
                        )}
                        
                        <div className="provider-properties">
                          <small>{getProviderProperties(provider)}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            
            <div className="provider-selection-info">
              <small className="form-hint">
                Selected: {selectedProviders.length} provider{selectedProviders.length !== 1 ? 's' : ''}
                {providerFilter && (
                  <span> • Showing: {filteredProviders.length} of {providers.length}</span>
                )}
              </small>
            </div>
          </div>
        </div>

        {/* Additional Information Card */}
        <div className="form-card additional-info-card">
          <div className="card-header">
            <h3 className="card-title">📝 Additional Details</h3>
            <span className="optional">(optional)</span>
          </div>
          <div className="card-content">
            <label htmlFor="info" className="form-label">
              Any specific requirements?
            </label>
            <textarea
              id="info"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              placeholder="Any additional details or specific requirements for this search..."
              className="form-textarea"
              rows={4}
              maxLength={1000}
            />
            <small className="form-hint">
              {info.length}/1000 characters
            </small>
          </div>
        </div>

        {/* Submit Card - Full Width */}
        <div className="form-card submit-card">
          <div className="card-content">
            {/* Error/Success Messages */}
            {error && (
              <div className="error-message">
                <p>⚠️ {error}</p>
              </div>
            )}

            {success && (
              <div className="success-message">
                <p>✅ {success}</p>
              </div>
            )}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={submitting || selectedProviders.length === 0 || searchTerm.trim().length < 3}
            >
              {submitting ? (
                <>
                  <div className="btn-spinner"></div>
                  Submitting Request...
                </>
              ) : (
                <>
                  🚀 Submit Request
                </>
              )}
            </button>
          </div>
        </div>

        {/* Previous Requests Card - Full Width */}
        {userRequests.length > 0 && (
          <div className="form-card previous-requests-card">
            <div className="card-header">
              <h3 className="card-title">📋 Your Recent Requests</h3>
              <div className="items-per-page-container">
                <label htmlFor="request-items-per-page">Items per page:</label>
                <select
                  id="request-items-per-page"
                  value={requestPagination.limit}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  className="items-per-page-select"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            <div className="card-content">
              <div className="requests-list">
                {userRequests.map((request) => (
                  <div key={request.id} className="request-item">
                    <div className="request-content">
                      <div className="request-info">
                        <div className="request-term">
                          <strong>"{request.search_term}"</strong>
                        </div>
                        <div className="request-providers">
                          Providers: {request.providers.join(', ')}
                        </div>
                        <div className="request-status">
                          Status: {request.active ? (
                            <span className="status-active">🟢 Active</span>
                          ) : (
                            <span className="status-inactive">🔴 Inactive</span>
                          )}
                        </div>
                      </div>
                      <div className="request-actions">
                        {request.active ? (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={activatingRequest === request.id}
                            onClick={() => handleActivateRequest(request.id)}
                          >
                            {activatingRequest === request.id ? (
                              <>
                                <div className="btn-spinner"></div>
                                Deactivating...
                              </>
                            ) : (
                              '⏸️ Deactivate'
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={activatingRequest === request.id}
                            onClick={() => handleActivateRequest(request.id)}
                          >
                            {activatingRequest === request.id ? (
                              <>
                                <div className="btn-spinner"></div>
                                Activating...
                              </>
                            ) : (
                              '✅ Activate'
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-danger"
                          disabled={deletingRequest === request.id}
                          onClick={() => setShowDeleteConfirm(request.id)}
                        >
                          {deletingRequest === request.id ? (
                            <>
                              <div className="btn-spinner"></div>
                              Deleting...
                            </>
                          ) : (
                            '🗑️ Delete'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {requestPagination.pages > 1 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Showing {requestPagination.offset + 1}-{Math.min(requestPagination.offset + requestPagination.limit, requestPagination.total)} of {requestPagination.total} requests
                  </div>
                  <div className="pagination-controls">
                    <button
                      onClick={() => handleRequestPageChange(0)}
                      disabled={requestPagination.offset === 0}
                    >
                      First
                    </button>
                    <button
                      onClick={() => handleRequestPageChange(requestPagination.offset - requestPagination.limit)}
                      disabled={requestPagination.offset === 0}
                    >
                      Previous
                    </button>
                    <span className="page-info">
                      Page {Math.floor(requestPagination.offset / requestPagination.limit) + 1} of {requestPagination.pages}
                    </span>
                    <button
                      onClick={() => handleRequestPageChange(requestPagination.offset + requestPagination.limit)}
                      disabled={requestPagination.offset + requestPagination.limit >= requestPagination.total}
                    >
                      Next
                    </button>
                    <button
                      onClick={() => handleRequestPageChange((requestPagination.pages - 1) * requestPagination.limit)}
                      disabled={requestPagination.offset + requestPagination.limit >= requestPagination.total}
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </form>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>⚠️ Confirm Delete</h3>
            <p>Are you sure you want to delete this request?</p>
            <p><strong>"{userRequests.find(r => r.id === showDeleteConfirm)?.search_term}"</strong></p>
            <p className="warning-text">This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deletingRequest === showDeleteConfirm}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => handleDeleteRequest(showDeleteConfirm)}
                disabled={deletingRequest === showDeleteConfirm}
              >
                {deletingRequest === showDeleteConfirm ? (
                  <>
                    <div className="btn-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestForm;
